import React, {Fragment, useCallback, useEffect, useMemo, useState} from 'react'
import Lottie from 'lottie-react'
import styled from 'styled-components'
import {translate} from 'i18n'
import _ from 'lodash'
import {requestData, requestDataPayload} from 'services'
import {
  CartPriceDetail,
  FontSizeType,
  GiftShopContentData,
  GiftShopFirstPurchaseHost,
  GiftshopContentDataPackage,
  WindowModeType,
} from 'types'
import {clearUserCache, showSnackbar, useDidMount, useHistory} from 'utils'
import {useWindowMode} from 'windows'
import {Paragraph} from 'common/components'
import {SERVICE_CANCELLATION_CART_PREVIEW} from 'consts'
import convertUnit from 'lib/unit'
import {useSelector} from 'lib/redux'
import {GiftShopCartContentProps} from './GiftShopCartContentProps'
import {GiftShopCartContentEmpty} from '../ContentEmpty'
import {
  GiftShopCartContentHeader,
  GiftShopCartAvailableContentHeader,
} from '../ContentHeader'
import {GiftShopCartContentItem} from '../ContentItem'
import {GiftShopCartSummary} from '../Summary'
import animationData from '../../../../../assets/lottie/activity_indicator.json'
import GiftShopCartPackageContentItem from '../ContentItem/GiftShopCartPackageContentItem'

interface StyledContinueContainerProps {
  mode: WindowModeType
}

const StyledContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 740;
  height: 100%;
  background-color: ${({theme}) => theme.white_1};
`

const StyledScrollContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 740;
  height: 100%;

  overflow-y: scroll;

  ::-webkit-scrollbar {
    width: ${convertUnit(25)};
  }
  ::-webkit-scrollbar-thumb {
    background-color: ${({theme}) => theme.primary_5};
    background-clip: content-box;
    border: ${convertUnit(8)} solid ${({theme}) => theme.white_1};
    border-radius: ${convertUnit(16)};
  }
`

const StyledHostHeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${({theme}) => theme.white_1};
`

const StyledHostText = styled(Paragraph)`
  padding-top: ${convertUnit(20)};
  padding-left: ${convertUnit(20)};
`

const StyledFooterPanthom = styled.div`
  height: ${convertUnit(100)};
`

const StyledContinueContainer = styled.div<StyledContinueContainerProps>`
  ${({theme, mode}) =>
    mode === 'website'
      ? {
          backgroundColor: theme.white_2,
          flex: 450,
          padding: convertUnit(50),
        }
      : {position: 'sticky', width: '100%', bottom: 0}}
`

const StyledLottie = styled(Lottie)`
  width: ${convertUnit(100)};
  height: ${convertUnit(100)};
  margin: auto;
`

export default function GiftShopCartContent({
  stateSelect,
}: GiftShopCartContentProps) {
  const [select, setSelect] = stateSelect
  const stateData = useState<
    readonly (GiftShopContentData | GiftshopContentDataPackage)[]
  >([])
  const [firstPurchase, setFirstPurchase] = useState<
    GiftShopFirstPurchaseHost[]
  >([])
  const [data, setData] = stateData
  const mode = useWindowMode()
  const history = useHistory()
  const stateToggle = useState(false)
  const {kycStatus} = useSelector('yuserActivationState')
  const [loading, setLoading] = useState(true)
  const [priceLoaded, setPriceLoaded] = useState(false)
  const [price, setPrice] = useState<CartPriceDetail>({
    total: 0,
    original_total: 0,
    discounts: {},
    final_price: 0,
    service_fee: 0,
  })

  const handleLoadData = useCallback(async () => {
    requestDataPayload('giftshop_post_cart_preview', {
      data: {page: 1, limit: 100},
      onRequestSuccess: ({status, data: {result}}) => {
        if (status === 200) {
          setFirstPurchase(result.available_first_purchase)
        }
      },
    })
      .then((response) => {
        if (typeof response !== 'string' && response?.status === 200) {
          setData(
            response.data.result.data.map((item) => ({...item, is_cart: true})),
          )
        }
      })
      .finally(() => {
        setLoading(false)
      })
  }, [setData])

  const handleGetTotalPrice = useCallback(
    async ({
      items,
      callback,
      updatePriceLoadedState,
    }: {
      items: readonly (GiftShopContentData | GiftshopContentDataPackage)[]
      callback?: (priceData: CartPriceDetail | null) => void
      updatePriceLoadedState?: boolean
    }) => {
      if (updatePriceLoadedState) setPriceLoaded(false)
      await requestData('giftshop_post_cart_preview', {
        cancelId: SERVICE_CANCELLATION_CART_PREVIEW,
        data: {
          page: 1,
          limit: 100,
          selected_products: items.map((item) => ({
            id: item.product_id,
            type_id: item.product_type_id,
          })),
        },
        onRequestSuccess: ({status, data: {result}}) => {
          if (status === 200) {
            const newSelectedDiscount: Record<string, number> = {}
            result.data.forEach((datum) => {
              if (datum.product_type !== 'content') return

              newSelectedDiscount[datum.product_id] = datum.price
            })
            const pricing: CartPriceDetail = {
              total: result.total_price,
              original_total: result.total_original_price,
              discounts: newSelectedDiscount,
              final_price: result.final_price,
              service_fee: result.service_fee,
            }
            setPrice(pricing)
            callback && callback(pricing)
          }
        },
        onRequestFailed: () => {
          callback && callback(null)
        },
      })
      if (updatePriceLoadedState) setPriceLoaded(true)
    },
    [],
  )

  const handleSelect = useCallback(
    (item: GiftShopContentData | GiftshopContentDataPackage) => {
      setPriceLoaded(false)
      setSelect((prev) =>
        !prev.some(
          (v) =>
            v.product_id === item.product_id &&
            v.product_type_id === item.product_type_id,
        )
          ? [...prev, item]
          : prev.filter(
              (value) =>
                value.product_id !== item.product_id ||
                value.product_type_id !== item.product_type_id,
            ),
      )
    },
    [setSelect],
  )

  const getSection = useMemo(
    () =>
      data.reduce((prev, curr) => {
        const host =
          (curr.product_type === 'content' &&
            curr.tags?.find((v) => v.type === 'host')?.name) ||
          ''

        if (prev[host]) {
          prev[host].push(curr)
        } else {
          prev[host] = [curr]
        }

        return prev
      }, {} as Record<string, (GiftShopContentData | GiftshopContentDataPackage)[]>),
    [data],
  )

  const firstPurchaseContent = useMemo(() => {
    if (!firstPurchase.length) return []

    const map: Record<string, GiftShopContentData> = {}

    for (const item of select) {
      if (item.product_type === 'content') {
        const hostTag = (item.tags ?? []).find(
          (v) => v.type === 'host' || v.type === 'tree',
        )?.name
        if (
          hostTag &&
          firstPurchase.some((v) => v.tag_name === hostTag) &&
          !map[hostTag]
        ) {
          map[hostTag] = item
        }
      }
    }

    return Object.values(map)
  }, [firstPurchase, select])

  const handleContinue = useCallback(() => {
    clearUserCache('cart')
    handleGetTotalPrice({
      items: select,
      callback: (pricingDetail) => {
        if (!pricingDetail) {
          showSnackbar(translate('global:messageError'))
          return
        }

        history.push('payment_checkout', {
          data: select,
          firstPurchase: firstPurchaseContent,
          serviceFee: pricingDetail.service_fee,
          totalOriginalPrice: pricingDetail.original_total,
        })
      },
    })
  }, [firstPurchaseContent, handleGetTotalPrice, history, select])

  const handleClickPackage = useCallback(
    (item: GiftshopContentDataPackage) => {
      history.pushQuery({
        path: 'giftshop_package',
        state: {},
        queryObject: {
          tree_id: item.package_tree_id,
          creator_id: item.member_id,
        },
      })
    },
    [history],
  )

  const handleClickContent = useCallback(
    (item: GiftShopContentData) => {
      if (process.env.APP_ENV === 'production') return

      history.push('giftshop_cart_detail', {
        data: [...data.filter((datum) => datum.product_type === 'content')],
        selectedItemId: item.product_id,
      })
    },
    [data, history],
  )

  const handleKeyExtractor = useCallback(
    (item: GiftShopContentData | GiftshopContentDataPackage) => item.product_id,
    [],
  )

  const handleRenderContentHeader = useMemo(
    () =>
      select.length || data.length ? (
        <GiftShopCartContentHeader
          stateData={stateData}
          stateSelect={stateSelect}
        />
      ) : null,
    [data.length, select.length, stateData, stateSelect],
  )

  const handleRenderFreeContentHeader = useMemo(
    () => <GiftShopCartAvailableContentHeader tagName={firstPurchase} />,
    [firstPurchase],
  )

  const handleHostHeader = useCallback(
    (name: string, size?: FontSizeType) => (
      <StyledHostHeaderContainer>
        <StyledHostText color="gray_5" fontWeight="medium" fontSize={size}>
          {name}
        </StyledHostText>
      </StyledHostHeaderContainer>
    ),
    [],
  )

  const handleRenderItem = useCallback(
    (item: GiftShopContentData | GiftshopContentDataPackage) =>
      item.product_type === 'package' ? (
        <GiftShopCartPackageContentItem
          key={handleKeyExtractor(item)}
          isSelected={select.some(
            (v) =>
              v.product_type_id === item.product_type_id &&
              v.product_id === item.product_id,
          )}
          data={item}
          onSelect={() => handleSelect(item)}
          onClickItem={handleClickPackage}
        />
      ) : (
        <GiftShopCartContentItem
          key={handleKeyExtractor(item)}
          isSelected={select.some(
            (v) =>
              v.product_type_id === item.product_type_id &&
              v.product_id === item.product_id,
          )}
          data={{
            ...item,
            price: price.discounts[item.product_id] ?? item.price,
          }}
          onSelect={() => handleSelect(item)}
          onClickItem={handleClickContent}
        />
      ),
    [
      handleKeyExtractor,
      select,
      handleClickPackage,
      price.discounts,
      handleClickContent,
      handleSelect,
    ],
  )

  const renderListSection = useCallback(() => {
    const hostTag = getSection
      ? Object.keys(getSection).filter((item) => item !== '')
      : []
    const creatorData = getSection[''] || []
    return (
      <StyledScrollContainer>
        {hostTag.length ? (
          handleHostHeader(translate('global:host'), 'l')
        ) : (
          <></>
        )}
        {getSection &&
          hostTag.map((tag) => (
            <Fragment key={tag}>
              {handleHostHeader(tag)}
              {getSection[tag].map(handleRenderItem)}
            </Fragment>
          ))}
        {creatorData.length ? (
          handleHostHeader(translate('giftShop:creator'), 'l')
        ) : (
          <></>
        )}
        {creatorData.map(handleRenderItem)}
      </StyledScrollContainer>
    )
  }, [getSection, handleHostHeader, handleRenderItem])

  const renderCustomList = useMemo(
    () =>
      kycStatus === 'verified' ||
      kycStatus === 'verified_check' ||
      kycStatus === 'onhold' ? (
        data.length > 0 ? (
          renderListSection()
        ) : (
          <GiftShopCartContentEmpty />
        )
      ) : (
        <GiftShopCartContentEmpty />
      ),
    [data.length, kycStatus, renderListSection],
  )
  const renderFooter = useMemo(
    () => mode !== 'website' && <StyledFooterPanthom />,
    [mode],
  )

  const handleRenderSummary = useMemo(
    () => (
      <StyledContinueContainer mode={mode}>
        <GiftShopCartSummary
          stateToggle={stateToggle}
          select={select}
          totalPrice={price.total}
          onContinue={handleContinue}
          priceLoaded={priceLoaded}
        />
      </StyledContinueContainer>
    ),
    [handleContinue, mode, price.total, priceLoaded, select, stateToggle],
  )

  const handleDebounceCart = useMemo(
    () =>
      _.debounce(
        async (items: (GiftShopContentData | GiftshopContentDataPackage)[]) => {
          handleGetTotalPrice({
            items,
            updatePriceLoadedState: true,
          })
        },
        300,
      ),
    [handleGetTotalPrice],
  )

  useEffect(() => {
    handleDebounceCart(select)
    return () => {
      handleDebounceCart.cancel()
    }
  }, [handleDebounceCart, select])

  useDidMount(() => {
    handleLoadData()
  })

  return (
    <>
      <StyledContentContainer>
        {loading ? (
          <StyledLottie
            animationData={animationData}
            height={convertUnit(100)}
            width={convertUnit(100)}
          />
        ) : (
          <>
            {handleRenderContentHeader}
            {handleRenderFreeContentHeader}
            {renderCustomList}
            {renderFooter}
          </>
        )}
      </StyledContentContainer>
      {handleRenderSummary}
    </>
  )
}
